/**
 * Deep links to Profile → Broker Management with broker/account context.
 */

import type { TradeAccountOption } from "../contexts/ActiveBrokerAccountContext";
import { isTradeAccountCsvOnlyForUi } from "./tradeAccountSource";

export function normalizeProfileBrokerSlug(brokerName: string): string {
  const s = String(brokerName || "").toLowerCase().trim();
  const compact = s.replace(/[\s_-]+/g, "");
  if (compact === "liquid" || compact === "liquidbrokers") return "liquidbrokers";
  if (compact === "interactivebrokers") return "ibkr";
  // Spaces suggest a display label ("Liquid Brokers") → compact slug.
  if (/\s/.test(s) && compact) return compact;
  return s;
}

export type ProfileBrokerIntegrationTab = "sync" | "csv";

export type ProfileBrokerApiUrlOptions = {
  brokerName?: string | null;
  accountCode?: string | null;
  /** Which sub-tab to open when the broker supports both sync and CSV. */
  brokerTab?: ProfileBrokerIntegrationTab;
  /** @deprecated Use brokerTab: "csv" */
  csvImport?: boolean;
};

/** Sync tab vs CSV tab when user taps Import from the dashboard. */
export function resolveProfileBrokerTabForImport(options: {
  brokerSupportsApiSync?: boolean;
  activeAccount?: Pick<TradeAccountOption, "accountSource" | "lastBrokerSyncAt" | "lastCsvImportAt"> | null;
}): ProfileBrokerIntegrationTab {
  if (options.activeAccount && isTradeAccountCsvOnlyForUi(options.activeAccount)) {
    return "csv";
  }
  if (options.brokerSupportsApiSync) {
    return "sync";
  }
  return "csv";
}

export function buildProfileBrokerApiUrl(options: ProfileBrokerApiUrlOptions = {}): string {
  const q = new URLSearchParams({ tab: "broker-api" });
  const bn = (options.brokerName || "").trim();
  const ac = (options.accountCode || "").trim();
  if (bn) q.set("broker_name", normalizeProfileBrokerSlug(bn));
  if (ac) q.set("account_code", ac);
  const tab =
    options.brokerTab ?? (options.csvImport ? "csv" : undefined);
  if (tab === "csv") q.set("csv", "1");
  else if (tab === "sync") q.set("sync", "1");
  return `/profile?${q.toString()}`;
}

type BrokerCapabilityRow = { broker_name: string };

/**
 * Pick broker slug for Broker Management tab: URL param → active dashboard account → first capability.
 */
export function resolveProfileBrokerFromCapabilities(
  capabilities: BrokerCapabilityRow[],
  options: {
    search?: string;
    activeBrokerName?: string | null;
    pathname?: string;
  } = {}
): string {
  if (!capabilities.length) return "";
  const find = (slug: string) =>
    capabilities.find((b) => normalizeProfileBrokerSlug(b.broker_name) === slug)?.broker_name ?? "";

  const sp = new URLSearchParams(options.search ?? "");
  const onBrokerManagement =
    (options.pathname ?? "").includes("/profile/broker-api") ||
    sp.get("tab") === "broker-api";
  if (onBrokerManagement) {
    const want = (sp.get("broker_name") || sp.get("broker") || "").trim();
    if (want) {
      const hit = find(normalizeProfileBrokerSlug(want));
      if (hit) return hit;
    }
  }

  const active = (options.activeBrokerName || "").trim();
  if (active) {
    const hit = find(normalizeProfileBrokerSlug(active));
    if (hit) return hit;
  }

  return capabilities[0].broker_name;
}
